import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/** Superficie sollevata. Una sola elevazione in tutta l'app: o è piano, o è scheda. */
export function Card({ className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn('bg-ink-800 rounded-lg border border-[var(--hairline)] p-4', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

export interface SectionHeaderProps {
  title: string
  description?: string
  /** Occhiello tecnico sopra il titolo, es. "MODULO ATTIVO". */
  eyebrow?: string
  actions?: ReactNode
  className?: string
  /** `display` per il titolo di una vista, `title` per una sezione interna. */
  level?: 'display' | 'title'
}

export function SectionHeader({
  title,
  description,
  eyebrow,
  actions,
  className,
  level = 'title',
}: SectionHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow ? <p className="type-eyebrow mb-1.5">{eyebrow}</p> : null}
        <h2 className={level === 'display' ? 'type-display' : 'type-title'}>{title}</h2>
        {description ? (
          <p className="text-thread-400 text-sm mt-1.5 max-w-prose">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  /** Cosa fare adesso. Uno schermo vuoto è un invito ad agire, non un vicolo cieco. */
  description: string
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <p className="type-voice text-linen-100 text-lead">{title}</p>
      <p className="text-thread-400 text-sm max-w-sm">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

export interface SkeletonProps {
  className?: string
  /** Descrive cosa sta arrivando, letto dagli assistenti vocali al posto della forma. */
  label?: string
}

export function Skeleton({ className, label }: SkeletonProps) {
  return (
    <div
      role="status"
      aria-label={label ?? 'Caricamento in corso'}
      className={cn('bg-ink-700 animate-pulse rounded-md', className)}
    />
  )
}
